import './App.css';
import { ToastContainer } from 'react-toastify';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import "react-toastify/dist/ReactToastify.css";
import RootRoute from './pages/RootRoute';
import Tutorials from './pages/Tutorials';
import TutsPlayer from './pages/TutsPlayer';
import Kampanja from './pages/Kampanja';
import AuthPage from './pages/auth/AuthPage';
import Dashboard from './pages/admin/Dashboard';
import TenantDashboard from './pages/TenantDashboard';
import CreateSuperAdmin from './pages/auth/CreateSuperAdmin';
import VerifyEmail from './pages/auth/VerifyEmail';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import NotFound from './pages/NotFound';
import PublicWebShop from './pages/PublicWebShop';
import Snowflakes from './components/effects/Snowflakes';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setBusinessType, setUser, checkTokenExpiry } from './redux/features/authSlice';
import { WebSocketProvider } from './contexts/WebSocketContext';
import NotificationChecker from './components/notifications/NotificationChecker';
import ErrorBoundary from './components/ErrorBoundary';
import GoogleAnalyticsRouteTracker from './components/analytics/GoogleAnalyticsRouteTracker';
import { applyLanguageFromCountry, getCountryCodeFromProfile } from './utils/languageFromCountry';

function App() {
  const dispatch = useDispatch();
  const [initialized, setInitialized] = useState(false);

  // Inicijalizujemo business type i sesiju pri pokretanju aplikacije
  useEffect(() => {
    const initializeApp = () => {
      // Prvo, proverimo sesiju/autentifikaciju
      const profile = localStorage.getItem('profile');
      if (profile && checkTokenExpiry()) {
        try {
          const parsedProfile = JSON.parse(profile);
          
          // Postavimo korisnika u Redux state
          dispatch(setUser(parsedProfile));

          // Jezik prema zemlji naloga (rs → srpski, ostalo → engleski)
          applyLanguageFromCountry(getCountryCodeFromProfile(parsedProfile));
          
          // Zatim, proverimo business type
          let businessType = sessionStorage.getItem('businessType');
          
          // Ako nije pronađen u session storage, uzmimo iz profila
          if (!businessType) {
            if (parsedProfile.result && parsedProfile.result.businessType) {
              businessType = parsedProfile.result.businessType;
            } else if (parsedProfile.businessType) {
              businessType = parsedProfile.businessType;
            }
          }
          
          if (businessType) {
            dispatch(setBusinessType(businessType));
            sessionStorage.setItem('businessType', businessType);
          }
        } catch (e) {
          console.error("Error initializing app state:", e);
        }
      }
      
      // Označimo da je inicijalizacija završena
      setInitialized(true);
    };
    
    initializeApp();
  }, [dispatch]);
  
  useEffect(() => {
    const touchSession = () => {
      if (checkTokenExpiry()) {
        localStorage.setItem('lastActive', new Date().toISOString());
      }
    };

    touchSession();
    window.addEventListener('click', touchSession);
    window.addEventListener('keydown', touchSession);

    return () => {
      window.removeEventListener('click', touchSession);
      window.removeEventListener('keydown', touchSession);
    };
  }, []);

  // Ako nije inicijalizovano, prikažimo prazan div
  if (!initialized) {
    return <div className="app-loading"></div>;
  }

  return (
    <ErrorBoundary>
      <WebSocketProvider>
        <NotificationChecker />
        <div 
          className="font-['Inter',_sans-serif]"
          style={{
            backgroundColor: 'var(--color-background, #ffffff)',
            color: 'var(--color-text, #1f2937)',
            minHeight: '100vh'
          }}
        >
          <Snowflakes />
          <BrowserRouter future={{ v7_relativeSplatPath: true }}>
            <GoogleAnalyticsRouteTracker />
            <ToastContainer
              position="top-center"
              autoClose={4000}
              newestOnTop
              closeOnClick
              pauseOnHover
              draggable
              style={{ zIndex: 100000 }}
            />
            <Routes>
              {/* Public routes - moraju biti pre company slug ruta */}
              <Route path='/' element={<RootRoute />} />
              <Route path='/tutorials' element={<Tutorials />} />
              {/* /tuts/* mora biti pre /:companySlug/* — pušta video na toj strani, bez login preusmerenja */}
              <Route path='/tuts/*' element={<TutsPlayer />} />
              <Route path='/kampanja' element={<Kampanja />} />
              <Route path='/auth/*' element={<AuthPage />} />
              <Route path='/verify-email' element={<VerifyEmail />} />
              <Route path='/forgot-password' element={<ForgotPassword />} />
              <Route path='/reset-password' element={<ResetPassword />} />
              <Route path='/superadmin' element={<CreateSuperAdmin />} />
              
              {/* Public Web Shop route - mora biti pre company slug ruta */}
              <Route path='/:slug' element={<PublicWebShop />} />
              
              {/* Multi-tenant routes - direktno na root nivou: spintasker.com/:companySlug/dashboard */}
              <Route path='/:companySlug/*' element={<TenantDashboard />} />
              
              {/* Legacy route - za retroaktivnu kompatibilnost */}
              <Route path='/dashboard/*' element={<Dashboard />} />
              
              <Route path='*' element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </div>
      </WebSocketProvider>
    </ErrorBoundary>
  );
}

export default App;
